import { Link } from 'react-router-dom';
import { Heart, Sparkles, BookOpen, ArrowRight } from 'lucide-react';

/**
 * TopicalClusters
 * Strengthens topical authority across emotional communication, gratitude,
 * affirmations, relationships, digital legacy, and celebrating people alive.
 *
 * Renders two crawlable, visually quiet sections:
 *   1) Five semantic clusters of internal links (one source of truth)
 *   2) A three-route secondary CTA row routing users back into the product
 *
 * Designed to drop into any SEO/editorial page without altering visual identity.
 * Accepts `currentPath` to suppress self-links and avoid loop noise.
 */

interface ClusterLink {
  label: string;
  href: string;
  hint?: string;
}

interface Cluster {
  title: string;
  intro: string;
  links: ClusterLink[];
}

export const TOPICAL_CLUSTERS: Cluster[] = [
  {
    title: 'Gratitude, on purpose',
    intro: 'The small practice of telling people, specifically, what they have meant to you.',
    links: [
      { href: '/digital-gratitude', label: 'Digital gratitude', hint: 'The practice, defined' },
      { href: '/thank-you-message-for-parents', label: 'Thank you message for parents' },
      { href: '/heartfelt-message-to-mom', label: 'Heartfelt message to Mom' },
      { href: '/heartfelt-message-to-dad', label: 'Heartfelt message to Dad' },
    ],
  },
  {
    title: 'Affirmations & emotional wellness',
    intro: 'The inward half of the practice — the words you say to yourself, and the ones you let in.',
    links: [
      { href: '/affirmations-and-emotional-wellness', label: 'Affirmations & emotional wellness' },
      { href: '/affirmation-images', label: 'Build an affirmation', hint: 'Use the studio' },
      { href: '/treslog', label: 'TRESLOG — private reflection' },
    ],
  },
  {
    title: 'Celebrating people while they\u2019re alive',
    intro: 'Living tributes, weddings, anniversaries, milestones — the words you would have saved for later.',
    links: [
      { href: '/what-is-a-living-announcement', label: 'What is a Living Announcement?' },
      { href: '/living-tribute-ideas', label: 'Living tribute ideas' },
      { href: '/living-announcements', label: 'Browse example announcements' },
      { href: '/why-say-it-now-exists', label: 'Why Say It Now exists' },
    ],
  },
  {
    title: 'Heartfelt communication',
    intro: 'Real words for the hardest things — love, apology, repair, presence.',
    links: [
      { href: '/message-to-someone-you-love', label: 'Message to someone you love' },
      { href: '/apology-message-examples', label: 'Apology message examples' },
      { href: '/tools/heartfelt-message-generator', label: 'Heartfelt message generator' },
      { href: '/tools/apology-message-generator', label: 'Apology message generator' },
    ],
  },
  {
    title: 'Digital legacy',
    intro: 'What lasts. What gets left behind. What we say before we run out of time to say it.',
    links: [
      { href: '/digital-legacy-message', label: 'Digital legacy message' },
      { href: '/what-to-say-before-someone-dies', label: 'What to say before someone dies' },
      { href: '/why-say-it-now-exists', label: 'Why Say It Now exists' },
    ],
  },
];

const SECONDARY_CTAS = [
  {
    href: '/try',
    label: 'Create a Living Announcement',
    sub: 'Two minutes. Free. Built for one person.',
    Icon: Heart,
  },
  {
    href: '/affirmation-images',
    label: 'Build an affirmation',
    sub: 'Words you can return to on a hard day.',
    Icon: Sparkles,
  },
  {
    href: '/treslog',
    label: 'Open TRESLOG',
    sub: 'A quiet, private place to reflect.',
    Icon: BookOpen,
  },
] as const;

interface TopicalClustersProps {
  /** Current page path — links matching this are suppressed to avoid self-loops. */
  currentPath?: string;
  /** Optional eyebrow override. */
  eyebrow?: string;
}

export function TopicalClusters({
  currentPath,
  eyebrow = 'Explore the practice',
}: TopicalClustersProps) {
  return (
    <>
      {/* Topical clusters — semantic interlinking grid */}
      <section
        className="container max-w-5xl px-5 py-16 md:py-24"
        aria-labelledby="topical-clusters-heading"
      >
        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
          {eyebrow}
        </p>
        <h2
          id="topical-clusters-heading"
          className="font-heading text-2xl md:text-3xl tracking-tight text-foreground mb-10 max-w-2xl"
        >
          A small library for celebrating people while they&rsquo;re alive.
        </h2>

        <div className="grid gap-x-10 gap-y-12 md:grid-cols-2">
          {TOPICAL_CLUSTERS.map((cluster) => (
            <nav
              key={cluster.title}
              aria-label={cluster.title}
              className="border-t border-border pt-6"
            >
              <h3 className="font-heading text-lg md:text-xl text-foreground mb-2 tracking-tight">
                {cluster.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                {cluster.intro}
              </p>
              <ul className="space-y-2.5">
                {cluster.links
                  .filter((l) => l.href !== currentPath)
                  .map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        className="group inline-flex items-baseline gap-2 text-foreground hover:text-primary transition-colors"
                      >
                        <ArrowRight
                          aria-hidden
                          className="w-3.5 h-3.5 text-primary/70 shrink-0 translate-y-0.5 transition-transform group-hover:translate-x-0.5"
                        />
                        <span>
                          <span className="underline underline-offset-4 decoration-border group-hover:decoration-primary">
                            {link.label}
                          </span>
                          {link.hint && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {link.hint}
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
              </ul>
            </nav>
          ))}
        </div>
      </section>

      {/* Secondary CTAs — route every crawlable page back into the product */}
      <section
        className="container max-w-5xl px-5 pb-16 md:pb-24"
        aria-labelledby="begin-the-practice-heading"
      >
        <div className="border-t border-border pt-10">
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Begin the practice
          </p>
          <h2
            id="begin-the-practice-heading"
            className="font-heading text-2xl md:text-3xl tracking-tight text-foreground mb-8 max-w-2xl"
          >
            Three quiet ways to start, right now.
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {SECONDARY_CTAS.map(({ href, label, sub, Icon }) => (
              <Link
                key={href}
                to={href}
                className="group block rounded-2xl border border-border bg-card/40 hover:bg-card hover:border-primary/40 p-6 transition-colors"
              >
                <Icon
                  aria-hidden
                  className="w-5 h-5 text-primary mb-4 transition-transform group-hover:scale-110"
                />
                <div className="font-heading text-lg text-foreground mb-1 tracking-tight">
                  {label}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{sub}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-primary">
                  Begin
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
